/**
 * The feature's texts as JSON, and back.
 *
 * The fields and the JSON box are two views of the same content, so this module is the single
 * place that converts between them — the same arrangement What's New uses for its announcements.
 *
 * **The JSON carries content only**: the fifteen texts in the three languages, plus the entry
 * point's `appearance` and the two toast switches. The BU, the key, the use cases and the kill
 * switch are deliberately
 * absent. They belong to the identification step, and a key arriving from a pasted document is the
 * easiest way to end up with a feature the dashboard will never find. The exit questionnaire is
 * absent for a different reason: it is a survey, written in the survey builder, and the link to it
 * lives on the survey.
 *
 * The appearance and the toasts **are** in the document, and they have to be: whoever copies a
 * cadastro's JSON to seed the next one would otherwise recreate it without its icons, its badge,
 * its visibility or its two toasts, and nothing anywhere would say so — the new feature would
 * simply look, and go quiet, like the old default.
 *
 * Parsing is **lenient on purpose**. It runs on every keystroke, so it only rejects what cannot be
 * read at all (a syntax error, or a root that is not an object); anything missing becomes empty.
 * Strict validation would wipe the fields the moment someone typed an opening brace.
 *
 * @module Pages/BUs/OptInFeatures
 */

import type { OptInEntryBadge, OptInLanguage, OptInToastType } from '../Types/OptInFeatures.types';
import {
  OPT_IN_ENTRY_BADGES,
  OPT_IN_LANGUAGES,
  OPT_IN_TOAST_TYPES,
} from '../Types/OptInFeatures.types';
import type {
  OptInAppearanceDraft,
  OptInFeatureFormData,
  OptInTextsDraft,
  OptInToastsDraft,
} from './optInFeatureForm';
import {
  EMPTY_APPEARANCE,
  EMPTY_TEXTS,
  EMPTY_TOASTS,
  OPT_IN_TOAST_SIDES,
} from './optInFeatureForm';

/** The content half of the form — what the JSON box shows. */
export interface OptInTextsContent {
  texts: Record<OptInLanguage, OptInTextsDraft>;
  /** The entry point's icons, badges and visibility — one set, with no language. */
  appearance: OptInAppearanceDraft;
  /** Whether each side of the toggle raises a toast — one set, with no language. */
  toasts: OptInToastsDraft;
}

const TEXT_FIELDS = Object.keys(EMPTY_TEXTS) as (keyof OptInTextsDraft)[];

const emptyByLanguage = (): Record<OptInLanguage, string> => ({ en: '', pt: '', es: '' });

/**
 * One text in the three languages, as the document writes it.
 *
 * Language-major rather than field-major on purpose: filling all three languages of a sentence in
 * one place is the reason to paste a document at all instead of walking the tabs ten times.
 */
const textEntry = (
  texts: Record<OptInLanguage, OptInTextsDraft>,
  field: keyof OptInTextsDraft,
): Record<OptInLanguage, string> => ({
  en: texts.en[field],
  pt: texts.pt[field],
  es: texts.es[field],
});

/**
 * Content as the JSON the box shows.
 *
 * Key order is fixed, and follows the three surfaces, so editing a field does not reshuffle the
 * document under the cursor.
 */
export const serializeTexts = (content: OptInTextsContent): string => {
  const document: Record<string, unknown> = {};

  TEXT_FIELDS.forEach(field => {
    document[field] = textEntry(content.texts, field);
  });

  /*
    Last, and in one object, so the texts keep reading as a list of sentences. `null` is written
    out rather than omitted: in this document an absent key and an explicit `null` would look the
    same, and `null` is the value that means "the dashboard decides".
  */
  document['appearance'] = { ...content.appearance };
  /*
    Beside the appearance, and for the same reason: two values with no language. A document that
    carried the four toast sentences but not the switch would paste back a cadastro with the
    wording in place and both toasts silently off.
  */
  document['toasts'] = { ...content.toasts };

  return JSON.stringify(document, null, 2);
};

/** The document to start from: every field, in every language. */
export const createTextsTemplate = (): string =>
  JSON.stringify(
    {
      name: { en: 'New MDM menu', pt: 'Novo menu MDM', es: 'Nuevo menú MDM' },
      description: { en: '', pt: '', es: '' },
      enterCta: { en: '', pt: '', es: '' },
      exitCta: { en: '', pt: '', es: '' },
      badgeTooltip: { en: '', pt: '', es: '' },
      confirmTitle: { en: '', pt: '', es: '' },
      confirmBody: { en: '', pt: '', es: '' },
      confirmCta: { en: '', pt: '', es: '' },
      enableToastTitle: { en: '', pt: '', es: '' },
      enableToastBody: { en: '', pt: '', es: '' },
      optOutTitle: { en: '', pt: '', es: '' },
      optOutBody: { en: '', pt: '', es: '' },
      optOutCta: { en: '', pt: '', es: '' },
      disableToastTitle: { en: '', pt: '', es: '' },
      disableToastBody: { en: '', pt: '', es: '' },
      appearance: { ...EMPTY_APPEARANCE },
      toasts: { ...EMPTY_TOASTS },
    },
    null,
    2,
  );

/**
 * A prompt to paste into an AI, which answers with the document filled in.
 *
 * The template is interpolated from {@link createTextsTemplate}, not written out again: the
 * instruction and the shape it asks for cannot fall out of step that way.
 *
 * It explains what each field is for, because an AI writing a button label without knowing the
 * button has two states writes the same words twice.
 */
export const createTextsPrompt =
  (): string => `Você vai preencher um JSON que configura os textos de uma funcionalidade opcional
("opt-in") de um dashboard. A pessoa liga essa funcionalidade por vontade própria e
pode voltar atrás quando quiser.

FUNCIONALIDADE: <descreva aqui o que ela faz, para quem, e o que muda na tela>

Os textos aparecem em três lugares:

1. PONTO DE ENTRADA — um convite na barra lateral, antes de qualquer modal.
   \`name\` e \`description\` apresentam a funcionalidade. \`enterCta\` é o rótulo do botão
   enquanto ela está DESLIGADA ("Experimentar"); \`exitCta\` é o rótulo do MESMO botão
   com ela LIGADA ("Voltar ao antigo"). São dois estados de um botão só.
   \`badgeTooltip\` é o texto que aparece ao passar o mouse no selo desse botão
   ("Beta — em desenvolvimento"); deixe vazio se a funcionalidade não tem selo.

2. MODAL DE ATIVAR — o que a pessoa lê antes de ligar. \`confirmTitle\`, \`confirmBody\`,
   \`confirmCta\`. Diga o que muda e deixe claro que dá para voltar; isso reduz o medo de
   experimentar.
   \`enableToastTitle\` e \`enableToastBody\` são o avisinho que aparece DEPOIS de ligar,
   já no dashboard: uma confirmação curta, não um segundo convite. Deixe vazio se a
   funcionalidade não precisa avisar nada.

3. MODAL DE DESATIVAR — o que ela lê ao voltar atrás. \`optOutTitle\`, \`optOutBody\`,
   \`optOutCta\`. As PERGUNTAS que ela responde ao sair não estão aqui: são um questionário
   de saída, montado no módulo de Pesquisas.
   \`disableToastTitle\` e \`disableToastBody\` são o avisinho depois de DESLIGAR — o recibo
   de que a saída aconteceu. Nada de tentar reconquistar quem saiu.

Regras:

- Preencha os três idiomas de cada texto: \`en\` (inglês), \`pt\` (português do Brasil),
  \`es\` (espanhol). Traduza o sentido, não palavra a palavra.
- Rótulos de botão: no máximo 4 palavras. Títulos: uma linha. Corpo de modal: uma ou
  duas frases.
- Tom direto, segunda pessoa, sem jargão de produto e sem exclamação.
- NÃO invente os blocos \`appearance\` e \`toasts\`: eles não têm idioma e são escolhidos na
  tela (ícone, selo, visibilidade do convite; e se cada toast dispara, e de que tipo).
  Devolva-os exatamente como vieram no template.
- Responda APENAS com o JSON, sem comentário nem cerca de código.

TEMPLATE (preencha exatamente esta estrutura):

${createTextsTemplate()}`;

export type ParseTextsResult =
  | { ok: true; content: OptInTextsContent }
  | { ok: false; message: string };

/**
 * Reads one text.
 *
 * Accepts two shapes, because two kinds of document get pasted here: the box's own
 * `{ en, pt, es }`, and a plain string — which is what copying a single field out of the admin
 * payload gives, where the base column is already English.
 */
const readText = (source: unknown): Record<OptInLanguage, string> => {
  if (typeof source === 'string') return { ...emptyByLanguage(), en: source };

  if (typeof source !== 'object' || source === null || Array.isArray(source)) {
    return emptyByLanguage();
  }

  const value = source as Record<string, unknown>;

  return {
    en: typeof value['en'] === 'string' ? value['en'] : '',
    pt: typeof value['pt'] === 'string' ? value['pt'] : '',
    es: typeof value['es'] === 'string' ? value['es'] : '',
  };
};

/** An icon variant, or `null`. Anything that is not a non-empty string is "not decided". */
const readIcon = (source: unknown): string | null => {
  if (typeof source !== 'string') return null;

  const trimmed = source.trim();

  return trimmed === '' ? null : trimmed;
};

/**
 * A badge, or `null`.
 *
 * An unknown word becomes `null` rather than failing the parse — the document is read on every
 * keystroke, and `"BET` on the way to `"BETA"` is not an error to shout about. The select is what
 * offers the real choices.
 */
const readBadge = (source: unknown): OptInEntryBadge | null =>
  OPT_IN_ENTRY_BADGES.find(badge => badge === source) ?? null;

/**
 * A toast type, or `null`.
 *
 * Same leniency as the badge, and for the same reason: the document is parsed on every keystroke,
 * so `"SUCC"` on the way to `"SUCCESS"` is not something to shout about. And `null` here is a real
 * answer — it is the off position — so an unreadable word landing on it is also the safe default:
 * the toast stays quiet rather than firing untitled.
 */
const readToastType = (source: unknown): OptInToastType | null =>
  OPT_IN_TOAST_TYPES.find(type => type === source) ?? null;

/** The toasts block, leniently. Absent means neither side says anything, which is the default. */
const readToasts = (source: unknown): OptInToastsDraft => {
  if (typeof source !== 'object' || source === null || Array.isArray(source)) {
    return { ...EMPTY_TOASTS };
  }

  const value = source as Record<string, unknown>;

  return {
    enableToastType: readToastType(value['enableToastType']),
    disableToastType: readToastType(value['disableToastType']),
  };
};

/**
 * The appearance block, leniently.
 *
 * Absent means the defaults, which is what a document written before 4.2.0 carries — and the
 * defaults are also what the BFF stores for a feature nobody has styled, so an old document still
 * describes the feature it described.
 *
 * `enterVisible` is the one field that is never `null`: anything other than an explicit `false`
 * reads as "keep inviting", because losing the invitation to a typo is the expensive direction.
 */
const readAppearance = (source: unknown): OptInAppearanceDraft => {
  if (typeof source !== 'object' || source === null || Array.isArray(source)) {
    return { ...EMPTY_APPEARANCE };
  }

  const value = source as Record<string, unknown>;

  return {
    enterIcon: readIcon(value['enterIcon']),
    exitIcon: readIcon(value['exitIcon']),
    enterBadge: readBadge(value['enterBadge']),
    exitBadge: readBadge(value['exitBadge']),
    enterVisible: value['enterVisible'] !== false,
  };
};

export const parseTexts = (raw: string): ParseTextsResult => {
  let parsed: unknown;

  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    return { ok: false, message: error instanceof Error ? error.message : String(error) };
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { ok: false, message: 'O JSON precisa ser um objeto na raiz.' };
  }

  const document = parsed as Record<string, unknown>;
  const texts: Record<OptInLanguage, OptInTextsDraft> = {
    en: { ...EMPTY_TEXTS },
    pt: { ...EMPTY_TEXTS },
    es: { ...EMPTY_TEXTS },
  };

  TEXT_FIELDS.forEach(field => {
    const entry = readText(document[field]);

    OPT_IN_LANGUAGES.forEach(language => {
      texts[language][field] = entry[language];
    });
  });

  return {
    ok: true,
    content: {
      texts,
      appearance: readAppearance(document['appearance']),
      toasts: readToasts(document['toasts']),
    },
  };
};

/**
 * Texts that render as a control or a heading in the dashboard, so an empty one is visible as a
 * gap rather than as nothing at all.
 *
 * `description`, `confirmBody` and `optOutBody` are absent on purpose: the dashboard simply draws
 * nothing for them, and listing them would bury the ones that leave a blank button on screen.
 */
const REQUIRED_FIELDS: (keyof OptInTextsDraft)[] = [
  'name',
  'enterCta',
  'exitCta',
  'confirmTitle',
  'confirmCta',
  'optOutTitle',
  'optOutCta',
];

/**
 * The toast titles are **conditionally** required, which is why they are not in the list above.
 *
 * With the type on "Nenhum" the toast does not fire and a blank title costs nothing. With a type
 * chosen it is the BFF's `400`, and this is where it should be caught: a pendência naming what
 * would happen beats a server error after the save button.
 *
 * **Base language only**, as the contract insists: a blank translation falls back to the base
 * column, so the box is never empty — and demanding it per language would make a feature stop
 * being editable in Spanish because somebody wrote a toast in English.
 *
 * Kept apart from {@link getTextProblems} because the two block differently: a missing translation
 * can still be saved with the feature switched off, and this cannot — the BFF refuses the pair
 * whatever `isActive` says, so offering "desative para salvar assim mesmo" here would send someone
 * straight into the `400` this exists to avoid.
 */
export const getToastProblems = (content: OptInTextsContent): string[] =>
  OPT_IN_TOAST_SIDES.filter(
    side =>
      content.toasts[side.typeField] !== null && content.texts.en[side.titleField].trim() === '',
  ).map(
    side =>
      `Escreva o ${side.titleField} em Inglês, ou deixe o tipo em «Nenhum»: com tipo escolhido e título vazio, o toast aparece como uma caixa vazia na tela de ${side.audience}.`,
  );

const LANGUAGE_NAME: Record<OptInLanguage, string> = {
  en: 'Inglês',
  pt: 'Português',
  es: 'Espanhol',
};

/**
 * What is still missing, listed rather than hidden behind a disabled button.
 *
 * Per language, because that is how a translation goes missing: the cadastro looks finished in the
 * language the person was typing in, and the gap only shows up for whoever opens the dashboard in
 * another one.
 */
export const getTextProblems = (content: OptInTextsContent): string[] => {
  const problems: string[] = [];

  REQUIRED_FIELDS.forEach(field => {
    OPT_IN_LANGUAGES.forEach(language => {
      if (content.texts[language][field].trim() === '') {
        problems.push(`Preencha o ${field} em ${LANGUAGE_NAME[language]}.`);
      }
    });
  });

  return problems;
};

/** The content half of a form, for the export button. */
export const contentFromForm = (form: OptInFeatureFormData): OptInTextsContent => ({
  texts: form.texts,
  appearance: form.appearance,
  toasts: form.toasts,
});
